/**
 * NOS-265: shared layout for the SPA public content pages — nav bar, footer,
 * org custom CSS (audit bug B4: it was fetched by two pages and injected by
 * none) and design-token CSS vars, around page-specific children.
 *
 * Pair with usePublicSiteShell(); pages render their own <SEOHead> via `head`
 * and their own content (including content-level loading/404 states) as
 * children. Deliberately NOT `all: "initial"` — unlike the template renderers,
 * these pages are Tailwind-styled and must keep inherited styles.
 */
import { PublicNavBar } from "@/components/PublicNavBar"
import { PublicFooter } from "@/components/public/PublicFooter"
import { parseAnnouncement } from "@/lib/announcement"
import { siteCssVars } from "@/lib/designTokens"
import type { PublicSiteShell } from "@/hooks/usePublicSiteShell"

interface PublicSitePageProps {
  shell: PublicSiteShell
  /** Page-specific <SEOHead> element (rendered inside the shell wrapper). */
  head?: React.ReactNode
  /** Extra wrapper classes (e.g. "bg-background"). */
  className?: string
  children: React.ReactNode
}

export function PublicSitePage({ shell, head, className, children }: PublicSitePageProps) {
  const {
    org,
    cust,
    navPages,
    navItems,
    loading,
    notFound,
    getPagePath,
    orgName,
    primaryColor,
    secondaryColor,
    fontFamily,
    logoScale,
  } = shell

  if (notFound || (!org && !loading)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Organization not found.</p>
      </div>
    )
  }

  // While the org is still resolving, the wrapper + page content (usually a
  // skeleton) render immediately — matching the pre-extraction pages, which
  // only gated the nav/footer on the org.
  return (
    <div
      className={`nos-site-root min-h-screen${className ? ` ${className}` : ""}`}
      style={
        {
          ...siteCssVars(cust),
          "--primary-color": primaryColor,
          "--secondary-color": secondaryColor,
          fontFamily,
        } as React.CSSProperties
      }
    >
      {head}
      {cust.custom_css ? <style>{cust.custom_css}</style> : null}
      {org && (
        <PublicNavBar
          orgName={orgName}
          logoUrl={org.logo_url}
          navItems={navItems}
          pages={navPages}
          primaryColor={primaryColor}
          navCtaText={cust.nav_cta_text ?? "Donate"}
          announcement={parseAnnouncement(cust.announcement)}
          fontFamily={fontFamily}
          getPagePath={getPagePath}
          orgId={org.id}
          logoScale={logoScale}
          stickyNav={cust.sticky_nav ?? true}
        />
      )}
      {children}
      {org && (
        <PublicFooter
          orgId={org.id}
          orgName={orgName}
          logoUrl={org.logo_url}
          missionStatement={org.mission_statement}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          fontFamily={fontFamily}
          pages={navPages}
          getPagePath={getPagePath}
          logoScale={logoScale}
        />
      )}
    </div>
  )
}
